import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import { PaginationResponse } from '../../../shared/models/pagination-response.model';
import { Product } from './models/product.model';
import * as ProductListActions from './products.actions';
import {
  selectCurrentPage,
  selectFilters,
  selectIsSortAsc,
  selectPageSize,
  selectSortBy,
} from './products.selectors';
import { ProductsState } from './products.state';
import { ProductService } from './products.service';

export const loadProducts$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    productsListService = inject(ProductService),
    store = inject(Store<ProductsState>)
  ) => {
    return actions$.pipe(
      ofType(
        ProductListActions.loadProducts,
        ProductListActions.setPage,
        ProductListActions.setPageSize,
        ProductListActions.setSearchTerm,
        ProductListActions.setCountryFilter,
        ProductListActions.setSorting
      ),
      withLatestFrom(
        store.select(selectFilters),
        store.select(selectCurrentPage),
        store.select(selectPageSize),
        store.select(selectSortBy),
        store.select(selectIsSortAsc)
      ),
      switchMap(([, filters, currentPage, pageSize, sortBy, isSortAsc]) =>
        productsListService.getProducts(filters, currentPage, pageSize, sortBy, isSortAsc).pipe(
          map((response: PaginationResponse<Product[]>) =>
            ProductListActions.loadProductsSuccess({ productsResponse: response })
          ),
          catchError(error =>
            of(
              ProductListActions.loadProductsFailure({
                error: error?.message ?? 'Unknown error loading products',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const productsListEffects: object = { loadProducts$ };
